import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/kit";
import { Pill } from "@/pages/Tickets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Clock, Lock, Loader2 } from "lucide-react";

const BE = API_BASE.replace(/\/api$/, "");
const DEPARTMENTS = ["IT", "Electrical", "HVAC", "Housekeeping", "Plumbing", "Security", "Furniture", "General"];

const Section = ({ title, children }) => <div className="bg-white rounded-2xl border border-slate-200 p-5"><p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">{title}</p>{children}</div>;

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [support, setSupport] = useState([]);
  const [asg, setAsg] = useState({ department: "", user_id: "" });
  const [transfer, setTransfer] = useState({ department: "", user_id: "", reason: "" });
  const [note, setNote] = useState({ text: "", internal: true });
  const [res, setRes] = useState({ notes: "", photos: [] });
  const [busy, setBusy] = useState("");
  const canManage = ["SUPER_ADMIN", "COMPANY_ADMIN", "IT_ADMIN", "SERVICE_HEAD"].includes(user.role);
  const canTransfer = canManage || user.role === "SUPPORT_TEAM";

  const load = () => api.get(`/tickets/${id}`).then((r) => { setT(r.data); setAsg((a) => ({ ...a, department: r.data.department })); setTransfer((x) => ({ ...x, department: r.data.department })); }).catch((e) => { toast.error(apiError(e)); navigate("/tickets"); });
  useEffect(() => { load(); }, [id]);
  const loadSupport = (dept) => api.get(`/tickets/support/users?department=${dept || ""}${t?.branch_id ? `&branch_id=${t.branch_id}` : ""}`).then((r) => setSupport(r.data)).catch(() => setSupport([]));

  const act = async (fn, key) => { setBusy(key); try { await fn(); await load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); } };
  const assign = () => act(async () => { if (!asg.user_id) throw { response: { data: { detail: "Select a support person" } } }; await api.post(`/tickets/${id}/assign`, asg); toast.success("Assigned"); }, "assign");
  const doTransfer = () => act(async () => { if (!transfer.reason) throw { response: { data: { detail: "Transfer reason required" } } }; await api.post(`/tickets/${id}/transfer`, transfer); toast.success("Transferred"); }, "transfer");
  const setStatus = (status) => act(async () => { await api.post(`/tickets/${id}/status`, { status }); toast.success(`Status: ${status}`); }, "status");
  const addNote = () => act(async () => { if (!note.text) return; await api.post(`/tickets/${id}/note`, note); setNote({ text: "", internal: true }); toast.success("Note added"); }, "note");
  const resolve = () => act(async () => { if (!res.notes) throw { response: { data: { detail: "Resolution notes required" } } }; await api.post(`/tickets/${id}/resolve`, res); setRes({ notes: "", photos: [] }); toast.success("Resolved"); }, "resolve");
  const uploadRes = async (e) => { const file = e.target.files?.[0]; if (!file) return; const fd = new FormData(); fd.append("file", file); try { const { data } = await api.post(`/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setRes((p) => ({ ...p, photos: [...p.photos, data.url] })); toast.success("Photo added"); } catch (e2) { toast.error(apiError(e2)); } };

  if (!t) return <Loader />;
  const R = t.requester || {};

  return (
    <div className="space-y-4">
      <button onClick={() => navigate("/tickets")} className="text-slate-500 hover:text-navy-900 flex items-center gap-1.5 text-sm" data-testid="ticket-back"><ArrowLeft size={16} /> All Tickets</button>
      <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-mono text-sm text-slate-400">{t.ticket_number}</p>
          <h1 className="font-display text-2xl font-bold text-navy-900">{t.issue}</h1>
          <p className="text-slate-500 text-sm">{t.category} · {t.department}</p>
        </div>
        <div className="text-right space-y-1">
          <Pill s={t.status} />
          <p className="text-sm font-semibold text-navy-900">{t.priority} priority</p>
          <p className={`text-xs font-semibold ${t.sla?.state === "overdue" ? "text-rose-600" : t.sla?.state === "due_soon" ? "text-amber-600" : "text-emerald-600"}`}>{t.sla?.label}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="Requester">
          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <span className="text-slate-400">Name</span><span className="text-navy-900 font-medium">{R.name || "-"}</span>
            <span className="text-slate-400">Company</span><span className="text-navy-900 font-medium">{R.company}</span>
            <span className="text-slate-400">Cabin</span><span className="text-navy-900 font-medium">{R.cabin}</span>
            <span className="text-slate-400">Branch</span><span className="text-navy-900 font-medium">{R.branch}</span>
            <span className="text-slate-400">Email</span><span className="text-navy-900 font-medium">{R.email}</span>
            <span className="text-slate-400">Phone</span><span className="text-navy-900 font-medium">{R.phone}</span>
          </div>
        </Section>
        <Section title="Issue">
          <p className="text-sm text-slate-600">{t.description || "No description provided."}</p>
          {t.photos?.length > 0 && <div className="flex gap-2 mt-3 flex-wrap">{t.photos.map((p, i) => <a key={i} href={`${BE}${p}`} target="_blank" rel="noreferrer"><img src={`${BE}${p}`} alt="" className="h-16 w-16 object-cover rounded-lg border" /></a>)}</div>}
          <p className="text-sm text-slate-500 mt-3">Assigned to: <b className="text-navy-900">{t.assigned_user_name || "Unassigned"}</b></p>
        </Section>
      </div>

      {!["Closed"].includes(t.status) && (
        <div className="grid lg:grid-cols-2 gap-4">
          {canManage && (
            <Section title="Assign">
              <div className="space-y-2">
                <Select value={asg.department} onValueChange={(v) => { setAsg({ department: v, user_id: "" }); loadSupport(v); }}>
                  <SelectTrigger data-testid="assign-department"><SelectValue placeholder="Department" /></SelectTrigger>
                  <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={asg.user_id} onValueChange={(v) => setAsg({ ...asg, user_id: v })} onOpenChange={(o) => o && support.length === 0 && loadSupport(asg.department)}>
                  <SelectTrigger data-testid="assign-user"><SelectValue placeholder="Support person" /></SelectTrigger>
                  <SelectContent>{support.length === 0 ? <div className="px-3 py-2 text-sm text-slate-400">No support users</div> : support.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.department})</SelectItem>)}</SelectContent>
                </Select>
                <Button onClick={assign} disabled={busy === "assign"} data-testid="assign-btn" className="bg-navy-900 w-full">{busy === "assign" ? <Loader2 className="animate-spin" size={16} /> : "Assign Ticket"}</Button>
              </div>
            </Section>
          )}
          <Section title="Update Status">
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setStatus("In Progress")} data-testid="status-inprogress">Start Work</Button>
              <Button size="sm" variant="outline" onClick={() => setStatus("On Hold")} data-testid="status-hold">On Hold</Button>
              <Button size="sm" variant="outline" onClick={() => setStatus("Assigned")}>Resume</Button>
            </div>
            <div className="mt-4 border-t border-slate-100 pt-3">
              <Label className="text-xs">Resolve ticket</Label>
              <Textarea className="mt-1" rows={2} placeholder="Resolution notes…" value={res.notes} onChange={(e) => setRes({ ...res, notes: e.target.value })} data-testid="resolve-notes" />
              <div className="flex items-center justify-between mt-2">
                <label className="text-xs text-slate-500 cursor-pointer"><input type="file" accept="image/*" className="hidden" onChange={uploadRes} /><span className="underline">Attach photo</span> {res.photos.length > 0 && `(${res.photos.length})`}</label>
                <Button size="sm" onClick={resolve} disabled={busy === "resolve"} data-testid="resolve-btn" className="bg-emerald-600 hover:bg-emerald-700">{busy === "resolve" ? <Loader2 className="animate-spin" size={16} /> : "Mark Resolved"}</Button>
              </div>
            </div>
          </Section>
        </div>
      )}

      {canTransfer && !["Closed", "Resolved"].includes(t.status) && (
        <Section title="Transfer to another department">
          <div className="flex flex-wrap gap-2 items-end">
            <div><Label className="text-xs">Department</Label>
              <Select value={transfer.department} onValueChange={(v) => { setTransfer({ ...transfer, department: v, user_id: "" }); loadSupport(v); }}>
                <SelectTrigger className="w-[150px]" data-testid="transfer-department"><SelectValue placeholder="Department" /></SelectTrigger>
                <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Input className="grow min-w-[160px]" placeholder="Reason for transfer" value={transfer.reason} onChange={(e) => setTransfer({ ...transfer, reason: e.target.value })} data-testid="transfer-reason" />
            <Button variant="outline" onClick={doTransfer} disabled={busy === "transfer"} data-testid="transfer-btn">{busy === "transfer" ? <Loader2 className="animate-spin" size={16} /> : "Transfer"}</Button>
          </div>
        </Section>
      )}

      <Section title="Notes">
        <div className="flex flex-wrap gap-2 items-center">
          <Input className="grow min-w-[200px]" placeholder="Add a note…" value={note.text} onChange={(e) => setNote({ ...note, text: e.target.value })} data-testid="note-text" />
          <label className="flex items-center gap-1.5 text-sm text-slate-500 cursor-pointer"><input type="checkbox" checked={note.internal} onChange={(e) => setNote({ ...note, internal: e.target.checked })} data-testid="note-internal" /> <Lock size={12} /> Internal</label>
          <Button variant="outline" onClick={addNote} data-testid="note-btn">Add</Button>
        </div>
      </Section>

      <Section title="Timeline">
        <div className="space-y-3 border-l-2 border-slate-100 pl-4">
          {t.timeline?.slice().reverse().map((e, i) => (
            <div key={i} className="text-sm relative">
              <span className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full bg-navy-300" />
              <p className="text-navy-900 font-medium flex items-center gap-1.5">{e.action}{e.internal && <Lock size={11} className="text-slate-400" />}</p>
              {e.detail && <p className="text-slate-500">{e.detail}</p>}
              <p className="text-xs text-slate-400 flex items-center gap-1"><Clock size={11} /> {new Date(e.ts).toLocaleString()}{e.actor && ` · ${e.actor}`}</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
